import { cloneElement, forwardRef, ReactElement, useEffect, useState } from 'react';

import { Button, Flex } from 'antd';
import { useTranslation } from 'react-i18next';

import { SearchOutlined } from '@ant-design/icons';

import {
  ButtonsWrapper,
  CheckIcon,
  CloseSearchButton,
  DividerStyled,
  DrawerItemWrapper,
  ItemLabel,
  NameWrapper,
  SearchWrapper,
  SelectStyled,
  StyledDrawer,
  StyledInput,
  StyledTitle,
} from './DrawerSelect.styled';
import { useMakeDefault } from './useMakeDefault';

export interface DrawerSelectOptions {
  value: string;
  label: string | React.ReactNode;
  disabled?: boolean;
}

interface DrawerSelectProps {
  title: string;
  onOk: (value: string | string[]) => void;
  onOkText: string;
  options: DrawerSelectOptions[];
  placeholder: string | React.ReactNode;
  value?: string | string[] | null;
  size?: 'default' | 'large';
  isMultiple?: boolean;
  field?: object;
  onClear?: () => void;
  disabled?: boolean;
  triggerComponent?: ReactElement<{
    disabled?: boolean;
    onClear?: () => void;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  }>;
  children?: ReactElement;
  makeDefaultAvailable?: boolean;
  allowClear?: boolean;
  defaultValue?: string;
  mode?: 'tags' | 'default';
  tokenSeparators?: string[];
  showSearch?: boolean;
  loading?: boolean;
}

const DrawerSelect = forwardRef<HTMLDivElement, DrawerSelectProps>(
  (
    {
      title,
      onOk,
      onOkText,
      options,
      placeholder,
      value,
      size = 'default',
      isMultiple = false,
      field,
      onClear,
      disabled,
      triggerComponent,
      children,
      makeDefaultAvailable,
      allowClear,
      defaultValue,
      mode,
      tokenSeparators,
      showSearch = false,
      loading,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [activeItem, setActiveItem] = useState<string | string[] | undefined>(
      value === null || value === '' ? undefined : value,
    );
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearchActive, setIsSearchActive] = useState(false);
    const showCheckIconConditions = !makeDefaultAvailable;

    const { makeDefault, defaultVenue } = useMakeDefault(activeItem);

    const filteredOptions = showSearch
      ? options.filter((option) => String(option.label).toLowerCase().includes(searchTerm.toLowerCase()))
      : options;

    useEffect(() => {
      setActiveItem(
        isMultiple
          ? Array.isArray(value)
            ? value.filter((val): val is string => typeof val === 'string')
            : value && value !== null && typeof value === 'string'
              ? [value]
              : []
          : value === null || value === ''
            ? undefined
            : value,
      );
    }, [value, isMultiple, onClear]);

    const handleOpenDrawer = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();
      !disabled && setOpen(true);
    };
    const handleClose = () => {
      setOpen(false);
      if (showSearch) {
        setSearchTerm('');
        setIsSearchActive(false);
      }
    };

    const handleClear = () => {
      if (onClear) {
        onClear();
      } else {
        onOk(isMultiple ? [] : '');
        setActiveItem(undefined);
      }
    };

    const handleSelectItem = (id: string) => {
      if (isMultiple) {
        setActiveItem((prev) => {
          const selected = Array.isArray(prev) ? prev : [];
          return selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id];
        });
      } else {
        setActiveItem(id);
      }
    };

    const handleConfirm = () => {
      if (isMultiple) {
        const selectedValues = options.filter((o) => (activeItem as string[])?.includes(o.value)).map((o) => o.value);
        onOk(selectedValues);
      } else {
        onOk(activeItem as string);
      }
      handleClose();
    };

    const handleSearchFocus = () => {
      setIsSearchActive(true);
    };

    const handleSearchBlur = () => {
      if (!searchTerm) {
        setIsSearchActive(false);
      }
    };

    const handleSearchClear = () => {
      setSearchTerm('');
      setIsSearchActive(false);
    };

    return (
      <div ref={ref} data-testid="drawer-select">
        {triggerComponent ? (
          cloneElement(triggerComponent, {
            disabled,
            onClear: handleClear,
            onClick: (e: React.MouseEvent<HTMLElement>) => {
              handleOpenDrawer(e as React.MouseEvent<HTMLDivElement>);
            },
          })
        ) : (
          <SelectStyled
            {...field}
            options={options}
            mode={mode === 'tags' ? 'tags' : isMultiple ? 'multiple' : undefined}
            value={
              isMultiple
                ? Array.isArray(value)
                  ? value.filter((val): val is string => typeof val === 'string')
                  : value && value !== null && typeof value === 'string'
                    ? [value]
                    : undefined
                : value === null || value === ''
                  ? undefined
                  : value
            }
            placeholder={placeholder}
            open={false}
            onClick={handleOpenDrawer}
            onClear={handleClear}
            allowClear={allowClear}
            disabled={disabled}
            defaultValue={defaultValue}
            tokenSeparators={tokenSeparators}
            loading={loading}
          />
        )}

        <StyledDrawer
          size={size}
          title={
            showSearch && isSearchActive ? (
              <SearchWrapper>
                <StyledInput
                  placeholder={t('general.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onClear={handleSearchClear}
                />
                <CloseSearchButton onClick={handleSearchClear} />
              </SearchWrapper>
            ) : showSearch ? (
              <SearchWrapper>
                <StyledTitle level={5}>{title}</StyledTitle>
                <Button type="text" variant="text" icon={<SearchOutlined />} onClick={handleSearchFocus} />
              </SearchWrapper>
            ) : (
              <StyledTitle level={5}>{title}</StyledTitle>
            )
          }
          onClose={handleClose}
          open={open}
          footer={
            <ButtonsWrapper>
              <Button block type="default" onClick={handleClose}>
                {t('general.cancel')}
              </Button>
              <Button block type="primary" onClick={handleConfirm}>
                {onOkText}
              </Button>
            </ButtonsWrapper>
          }
        >
          <Flex vertical>
            {filteredOptions.map((option) => {
              const isSelected = Array.isArray(activeItem)
                ? activeItem.includes(option.value)
                : activeItem === option.value;
              return (
                <DrawerItemWrapper
                  key={option.value}
                  $isDisabled={option.disabled}
                  $isSelected={isSelected}
                  onClick={() => !option.disabled && handleSelectItem(option.value)}
                >
                  <NameWrapper>
                    <ItemLabel $isDisabled={option.disabled}>{option.label}</ItemLabel>
                    {isSelected && showCheckIconConditions ? <CheckIcon /> : null}
                    {(isSelected && makeDefaultAvailable) || option.value === defaultVenue?.id
                      ? makeDefault(option)
                      : null}
                  </NameWrapper>
                  <DividerStyled />
                </DrawerItemWrapper>
              );
            })}
            {children}
          </Flex>
        </StyledDrawer>
      </div>
    );
  },
);

DrawerSelect.displayName = 'DrawerSelect';
export { DrawerSelect };
